import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import homeSmartApi from '@/plugins/HomeSmartApi';
import { Location } from '@/modules/backend/Entities/Location';
import { CreateLocationData } from '@/modules/backend/Dto/CreateLocationData';

@Module({ store, namespaced: true, name: 'dashboard/locations' })
export class LocationsModule extends VuexModule {
  private loading = false;
  private items: { [buildingId: number]: Location[] } = {};

  public get Locations(): Location[] {
    return Object.values(this.items).flat();
  }

  public get LocationsByBuildingId(): (buildingId: number) => Location[] {
    return (buildingId: number): Location[] => (this.items[buildingId] ?? []);
  }

  public get LocationById(): (locationId: number) => Location | undefined {
    return (locationId: number): Location | undefined => {
      return Object.entries(this.items).reduce((accum: Location | undefined, [, items]) => {
        if (accum) {
          return accum;
        }

        return items.find((location) => location.getId() === locationId);
      }, undefined);
    };
  }

  @Action
  public async Create(payload: CreateLocationData): Promise<Location> {
    const response = await homeSmartApi.Locations.Create(payload);
    this.context.commit('Add', response);
    return response;
  }

  @Action
  public async Fetch(buildingId: number): Promise<Location[]> {
    if (!Number.isInteger(buildingId)) {
      return [];
    }

    const response: Location[] = await homeSmartApi.Locations.List(buildingId);
    this.context.commit('Set', response);
    return response;
  }

  @Action
  public async FetchSingle(locationId: number): Promise<Location | undefined> {
    if (!Number.isInteger(locationId)) {
      return undefined;
    }

    const response: Location | undefined = await homeSmartApi.Locations.GetOne(locationId);
    this.context.commit('Add', response);
    return response;
  }

  @Action
  public async Remove(locationId: number): Promise<void> {
    await homeSmartApi.Locations.Remove(locationId);
    this.context.commit('RemoveById', locationId);
  }

  @Mutation
  private Set(payload: Location[]): void {
    const items = payload.reduce((accum, item: Location) => {
      accum[item.getBuilding().getId()] = [
        ...accum[item.getBuilding().getId()] || [],
        item
      ];
      return accum;
    }, {} as {[id: number]: Location[]});

    this.items = { ...this.items, ...items };
  }

  @Mutation
  private Add(payload: Location): void {
    if (!Object.prototype.hasOwnProperty.call(this.items, payload.getBuilding().getId())) {
      this.items = {
        ...this.items,
        [payload.getBuilding().getId()]: [payload]
      };
      return;
    }

    const existingIds = this.items[payload.getBuilding().getId()].map(item => item.getId());
    if (existingIds.indexOf(payload.getId()) === -1) {
      this.items = {
        ...this.items,
        [payload.getBuilding().getId()]: [
          ...this.items[payload.getBuilding().getId()],
          payload
        ]
      };
    }
  }

  @Mutation
  private RemoveById(locationId: number): void {
    this.items = Object.fromEntries(Object.entries(this.items).map((entries) => ([
      entries[0], entries[1].filter((location) => location.getId() !== locationId)
    ])));
  }

  @Mutation
  private setLoading(loading: boolean): void {
    this.loading = loading;
  }
}
